import { useMemo, useState } from 'react';
import {
  Content,
  EmptyState,
  Header,
  Link,
  LinkButton,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  Button,
  ButtonGroup,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import type { FacetOption } from '../platform/FacetRow';
import { FacetRow } from '../platform/FacetRow';
import { kindByTemplateName } from '../platform/kinds';
import type { TemplateView } from '../platform/templates';
import {
  orderable,
  shouldShowTenant,
  useTemplates,
} from '../platform/templates';
import { useAuthorizedTenants } from './tenants';

/** Cards give the kind icon room to work; the table rows do not. */
const CARD_ICON = { width: 34, height: 34, fontSize: 12 };

type LayoutId = 'cards' | 'list';

const plural = (count: number, word: string) =>
  count === 1 ? word : `${word}s`;

/**
 * The tenant belongs in the subtitle because what is on offer depends on it:
 * template families are enabled per tenant, so two people on the same platform
 * see different lists. Users with several tenants are named all of them rather
 * than one being picked for them, and when the lookup comes back empty the
 * clause is dropped instead of a placeholder tenant being invented.
 */
const subtitleFor = (tenants: string[]): string => {
  if (tenants.length === 0) return 'Templates you can order from the platform';
  return `Available templates for ${plural(
    tenants.length,
    'tenant',
  )} ${tenants.join(', ')}`;
};

const useStyles = makeStyles(theme => ({
  facets: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  grow: {
    flex: 1,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(2),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },
  name: {
    fontWeight: 600,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  description: {
    color: theme.palette.text.secondary,
    // The grid row is as tall as its tallest card, so a long description on one
    // template would otherwise push every neighbouring card's tags down with it.
    flex: 1,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  choose: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  wrapper: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    overflow: 'hidden',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  descriptionCell: {
    color: theme.palette.text.secondary,
    maxWidth: 420,
  },
}));

const TemplateTags = ({ tags }: { tags: string[] }) => {
  const classes = useStyles();
  return (
    <div className={classes.tags}>
      {tags.map(tag => (
        <Chip key={tag} label={tag} size="small" variant="outlined" />
      ))}
    </div>
  );
};

/**
 * A template is served per tenant, so a user with two tenants is offered the
 * same title twice. Naming the tenant is what tells those two apart - but only
 * when there are two: on the single-tenant estate that is every other user, the
 * caption would repeat what the page subtitle already says.
 */
type TemplateProps = { template: TemplateView; showTenant: boolean };

/**
 * The line under the title: the template's own name, and the tenant when that
 * is what tells this card from the one beside it.
 *
 * The tenant, not the namespace. They are the same string for everything the
 * IDP serves, while compatibility fixtures may sit in `default`, which names
 * no tenant and would read as one.
 */
const captionFor = (template: TemplateView, showTenant: boolean): string =>
  showTenant && template.tenant
    ? `${template.name} - ${template.tenant}`
    : template.name;

/** Browsing form: enough of each template to choose between them at a glance. */
const TemplateCard = ({ template, showTenant }: TemplateProps) => {
  const classes = useStyles();
  const kind = kindByTemplateName(template.name, template.familyId);

  return (
    <Link to={template.path} className={classes.card}>
      <kind.Icon style={CARD_ICON} />
      <Typography variant="subtitle1" className={classes.name}>
        {template.title}
      </Typography>
      <Typography variant="caption" className={classes.muted}>
        {captionFor(template, showTenant)}
      </Typography>
      {template.description && (
        <Typography variant="body2" className={classes.description}>
          {template.description}
        </Typography>
      )}
      <TemplateTags tags={template.tags} />
      <span className={classes.choose}>
        Choose
        <ArrowForwardIcon fontSize="small" />
      </span>
    </Link>
  );
};

/** Scanning form: one line each, for someone who knows what they came for. */
const TemplateRow = ({ template, showTenant }: TemplateProps) => {
  const classes = useStyles();
  const kind = kindByTemplateName(template.name, template.familyId);

  return (
    <TableRow hover>
      <TableCell>
        <div className={classes.nameCell}>
          <kind.Icon />
          <div>
            <Link to={template.path} className={classes.name}>
              {template.title}
            </Link>
            <Typography
              variant="caption"
              component="div"
              className={classes.muted}
            >
              {captionFor(template, showTenant)}
            </Typography>
          </div>
        </div>
      </TableCell>
      <TableCell className={classes.descriptionCell}>
        {template.description}
      </TableCell>
      <TableCell>
        <TemplateTags tags={template.tags} />
      </TableCell>
      <TableCell align="right">
        <LinkButton to={template.path} size="small" color="primary">
          Choose
        </LinkButton>
      </TableCell>
    </TableRow>
  );
};

/**
 * The template picker.
 *
 * Day-2 templates are left out: reconfigure, power and destroy act on a
 * component that already exists, so they are reached from that component's own
 * page and would only be noise in a list a user opens to make a new thing.
 * What remains is filtered by tag rather than searched, because the catalogue
 * grows a family at a time and a user browsing it does not yet know the words
 * to type.
 */
export const CreateTemplatesPage = () => {
  const classes = useStyles();
  const { loading, error, value: templates } = useTemplates();
  const tenants = useAuthorizedTenants();
  const [selected, setSelected] = useState<string[]>([]);
  const [layout, setLayout] = useState<LayoutId>('cards');

  const available = useMemo(() => orderable(templates), [templates]);

  // Counted over tenants, not namespaces. `default` is a namespace and not a
  // tenant, so counting namespaces made a single-tenant estate look like two
  // as soon as one app-shipped template was on offer - and every estate has
  // one, application-group - which turned the caption on for everybody and
  // labelled that template with a tenant nobody has.
  const showTenant = useMemo(() => shouldShowTenant(available), [available]);

  // Tags narrow with AND: each one a user adds is another thing the template
  // has to be, which is how "linux" plus "azure" reaches one card.
  const shown = useMemo(
    () =>
      available.filter(template =>
        selected.every(tag => template.tags.includes(tag)),
      ),
    [available, selected],
  );

  const tagOptions = useMemo<FacetOption[]>(() => {
    const counts = new Map<string, number>();
    for (const template of shown) {
      for (const tag of template.tags) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }

    return (
      [...new Set(available.flatMap(template => template.tags))]
        // A tag every orderable template carries cannot narrow anything, so it
        // is left out of the row entirely. Measured against the full set rather
        // than the current one, so the chips do not reshuffle under the cursor
        // as the selection tightens.
        .filter(tag => available.some(template => !template.tags.includes(tag)))
        .map(tag => ({ id: tag, label: tag, count: counts.get(tag) ?? 0 }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [available, shown]);

  const toggleTag = (tag: string) =>
    setSelected(previous =>
      previous.includes(tag)
        ? previous.filter(other => other !== tag)
        : [...previous, tag],
    );

  const clearTags = () => setSelected([]);

  return (
    // A page of its own, with the title and tenant line in the header band, the
    // way the prototype draws every view and the way Home and My stacks already
    // render. The scaffolder's own "Create" bar and its Templates/Tasks/Actions
    // tab strip are not part of that picture, which is why this is mounted
    // above the scaffolder page rather than inside its templates sub-page.
    <Page themeId="home">
      <Header title="Create a new component" subtitle={subtitleFor(tenants)} />
      <Content>
        {loading && <Progress />}

        {!loading && error && (
          <WarningPanel title="Could not load templates" severity="error">
            {error.message}
          </WarningPanel>
        )}

        {!loading && !error && available.length === 0 && (
          <EmptyState
            missing="content"
            title="No templates available"
            description="Nothing is enabled for your tenant yet. Template families are enabled per tenant by an operator."
          />
        )}

        {!loading && !error && available.length > 0 && (
          <>
            {tagOptions.length > 0 && (
              <div className={classes.facets}>
                <FacetRow
                  label="Tags"
                  options={tagOptions}
                  selected={selected}
                  onToggle={toggleTag}
                />
              </div>
            )}

            <div className={classes.toolbar}>
              <Typography variant="body2" className={classes.muted}>
                Showing {shown.length} of {available.length}{' '}
                {plural(available.length, 'template')}
              </Typography>
              <Button
                color="secondary"
                disabled={selected.length === 0}
                onClick={clearTags}
              >
                Clear tags
              </Button>
              <span className={classes.grow} />
              {/* The layout is the user's call, not ours: the same set of
                templates is a gallery to someone deciding and a list to
                someone who already knows which one they want. */}
              <ButtonGroup size="small" aria-label="Template layout">
                <Button
                  variant={layout === 'cards' ? 'contained' : 'outlined'}
                  color={layout === 'cards' ? 'primary' : 'default'}
                  startIcon={<ViewModuleIcon />}
                  onClick={() => setLayout('cards')}
                >
                  Cards
                </Button>
                <Button
                  variant={layout === 'list' ? 'contained' : 'outlined'}
                  color={layout === 'list' ? 'primary' : 'default'}
                  startIcon={<ViewListIcon />}
                  onClick={() => setLayout('list')}
                >
                  List
                </Button>
              </ButtonGroup>
            </div>

            {shown.length === 0 && (
              <EmptyState
                missing="data"
                title="No template carries every selected tag"
                description="Drop one of the tags to widen the search."
                action={
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={clearTags}
                  >
                    Clear tags
                  </Button>
                }
              />
            )}

            {shown.length > 0 && layout === 'cards' && (
              <div className={classes.cards}>
                {shown.map(template => (
                  <TemplateCard
                    key={template.key}
                    template={template}
                    showTenant={showTenant}
                  />
                ))}
              </div>
            )}

            {shown.length > 0 && layout === 'list' && (
              <div className={classes.wrapper}>
                <Table size="small">
                  <TableBody>
                    {shown.map(template => (
                      <TemplateRow
                        key={template.key}
                        template={template}
                        showTenant={showTenant}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
